import React from 'react';
import parse, { domToReact } from 'html-react-parser';
import { SecurePdfViewer, SecurePdfDownload } from '../components/SecurePdfViewer';
import { SecurePdfViewDownload } from '../components/SecurePdfViewDownload';

/**
 * Utility to automatically secure PDF links in admin pages
 * This can be used to wrap existing content and secure PDF links
 */

/**
 * Process HTML content and replace protected PDF links with secure components
 * @param {string} htmlContent - HTML content from CMS
 * @param {string} pageUrl - Current page URL
 * @param {string} mode - 'view', 'download', or 'both' (default: 'both')
 * @returns {JSX.Element} - React element with secured PDF links
 */
export function securePdfContent(htmlContent, pageUrl, mode = 'both') {
  if (!htmlContent) return null;

  // Only secure PDFs on admin pages
  if (!isAdminPage(pageUrl)) {
    return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  }

  return parse(htmlContent, {
    replace: (domNode) => {
      if (
        domNode.name === 'a' &&
        domNode.attribs &&
        domNode.attribs.href &&
        domNode.attribs.href.toLowerCase().endsWith('.pdf') &&
        isProtectedPdfPath(domNode.attribs.href)
      ) {
        const { href, class: className, ...rest } = domNode.attribs;
        const children = domToReact(domNode.children);
        
        // Choose component based on mode
        let SecureComponent;
        if (mode === 'download') {
          SecureComponent = SecurePdfDownload;
        } else if (mode === 'view') {
          SecureComponent = SecurePdfViewer;
        } else {
          // Default: both view and download options
          SecureComponent = SecurePdfViewDownload;
        }
        
        return (
          <SecureComponent
            href={href}
            className={className}
            pageUrl={pageUrl}
            {...rest}
          >
            {children}
          </SecureComponent>
        );
      }
    }
  });
}

/**
 * Check if a PDF path is protected (in admin/private/secure folders)
 * @param {string} href - PDF URL
 * @returns {boolean} - True if protected
 */
function isProtectedPdfPath(href) {
  if (!href) return false;
  
  const path = href.toLowerCase();
  return path.includes('/admin/') || path.includes('/private/') || path.includes('/secure/');
}

/**
 * Higher-order component to automatically secure PDF links
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} - Wrapped component with PDF security
 */
export function withSecurePdfs(Component) {
  return function SecurePdfWrapper(props) {
    const { pageUrl, isDownload, ...otherProps } = props;
    // If the component has HTML content, secure it
    if (props.htmlContent) {
      const securedContent = securePdfContent(props.htmlContent, pageUrl, isDownload);
      return <Component {...otherProps} securedContent={securedContent} />;
    }
    return <Component {...props} />;
  };
}

/**
 * Hook to automatically secure PDF links in admin pages
 * @param {string} pageUrl - Current page URL
 * @param {string} mode - 'view', 'download', or 'both' (default: 'both')
 * @returns {Object} - Object with securePdfContent function
 */
export function useSecurePdfs(pageUrl, mode = 'both') {
  return {
    securePdfContent: (htmlContent) => securePdfContent(htmlContent, pageUrl, mode)
  };
}

/**
 * Simple function to check if current page is an admin page
 * @param {string} pageUrl - Current page URL
 * @returns {boolean} - True if admin page
 */
export function isAdminPage(pageUrl) {
  const adminPaths = [
    '/freemasonry/ritual',
    '/freemasonry/treasurers',
    '/freemasonry/secretaries',
    '/freemasonry/adelaide-406',
    '/freemasonry/aurora-australis-431',
    '/freemasonry/avalon-1718',
    '/freemasonry/ouroboros-2175',
    '/freemasonry/sydney-404',
    '/freemasonry/national'
  ];
  return adminPaths.some(path => pageUrl.startsWith(path));
} 